import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import leMoulinApi from "app/universal/utils/LeMoulinApi";

/**
 * Hook lecture seule qui récupère les Articles "Repas staff" pour la commande
 * groupée en cours d'édition (cf. lemoulinapi/docs/pro/repas-extra.md).
 *
 * Retour :
 *   {
 *     items: [{ id, productId, productName, quantity, category }],
 *     preview: { mode, nbExtras, chosenProductId, errors },
 *     loading: bool,
 *     error: ?string,
 *     refetch: () => void,
 *   }
 *
 * - `items` : les Articles déjà créés en BDD pour cette commande enfant
 *   (présents uniquement après que le devis ait été validé en statut 102,
 *   ou suite au cron `app:staff-meal-sync` qui les pré-crée à J+0..J+10).
 * - `preview` : ce qui SERAIT créé à la prochaine validation.
 *   - `mode = no_service` → aucune ligne service détectée, rien à prévoir.
 *   - `mode = normal` → 1er produit du roulement `repas-prestataire`.
 *   - `mode = creuziflette` → produit déjà commandé par le client.
 *   - `mode = missing_config` → aucun produit `repas-prestataire` configuré,
 *     l'admin doit attacher le tag à au moins 1 produit (Sonata ou
 *     `app:tag-repas-extra --b2b-ids=...`).
 *
 * Le hook ne fetch pas tant que `groupedOrderId` est null (cas création).
 */
export const useStaffMeals = (orderIndex) => {
  const groupedOrderId = useSelector((s) => s.orderEdition.groupedOrderId);
  const orderId = useSelector((s) => s.orderEdition.orders?.[orderIndex]?.id);

  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetch = useCallback(() => {
    if (!groupedOrderId) return;
    setLoading(true);
    setError(null);
    leMoulinApi.request.grouped
      .staffMeals(groupedOrderId)
      .then((res) => {
        // L'interceptor axios global (cf. state/apiService.js l.294) renvoie
        // déjà `response.data` à la place de l'objet axios complet. Donc
        // `res` est directement le body JSON — ici le tableau renvoyé par
        // l'endpoint.
        const data = Array.isArray(res) ? res : [];
        // L'endpoint renvoie un tableau d'objets { orderId, deliveryDate,
        // items, preview }. On filtre sur orderId pour ne garder que la
        // commande enfant de ce composant. Loose match (Number===Number)
        // car le state Redux peut stocker l'orderId en string selon le
        // chemin de chargement.
        const mine = orderId
          ? data.find((d) => Number(d.orderId) === Number(orderId))
          : null;
        setItems(mine?.items || []);
        setPreview(mine?.preview || null);
      })
      .catch((err) => {
        setError(err?.message || String(err));
        setItems([]);
        setPreview(null);
      })
      .finally(() => setLoading(false));
  }, [groupedOrderId, orderId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { items, preview, loading, error, refetch: fetch };
};

export default useStaffMeals;
